/**
 * Fragments stores IFC classes upper-cased ('IFCBEAMSTANDARDCASE').
 * prettyIfcClass() turns them back into schema casing ('IfcBeamStandardCase')
 * by splitting the name into known words. Unknown tails are kept capitalised,
 * so the worst case is 'IfcSomethingunknown'.
 */

const WORDS = `
Accessory Actuator Air Alarm Alignment Anchor Annotation Appliance Assembly Audio Axis Bar Beam Bearing Body Boiler
Borehole Boundary Box Bridge Building Built Burner Cable Caisson Carrier Case Chiller Chimney Civil Coil Column
Communications Compressor Condenser Conduit Connection Controller Conversion Cooled Cooling Course Covering Curtain Cut
Damper Deep Device Discrete Distribution Door Duct Earthworks Electric Electrical Element Elemented Energy Equipment
Evaporative Evaporator Exchanger External Facility Fan Fastener Fill Filter Fire Fitting Fixture Flow Footing Foundation
Furnishing Furniture Generator Geographic Geomodel Geoslice Geotechnical Grid Group Heat Humidifier Impact Interceptor
Isolator Junction Kerb Lamp Light Linear Liquid Marine Mechanical Medical Member Mesh Mobile Mooring Motor Moving
Navigational Opening Outlet Part Pavement Pile Pipe Plant Plate Port Positioning Projection Project Protective
Protection Proxy Pump Rail Railing Railway Ramp Referent Reinforced Reinforcing Road Roof Sanitary Segment Sensor Shading
Sign Signal Site Slab Solar Source Space Spatial Stack Stair Standard Storey Stratum Structure Structural Suppression
Surface Switching System Tank Tendon Terminal Track Transformer Transport Treatment Tube Tower Unitary Valve Vehicle
Vibration Virtual Visual Wall Waste Window Zone Flight Feature Grade Level Line Load Point Product Proxy Rel Representation
Type Unit Volume Voiding Wire Way Profile Material Property Set Quantity Placement Local Shape Context Owner History Person
Organization Application Direction Cartesian Polyline Extruded Area Solid Axis2 Placement3D Placement2D Rectangle Circle
`
  .split(/\s+/)
  .filter(Boolean)

const VOCAB = new Map<string, string>()
for (const w of WORDS) VOCAB.set(w.toUpperCase(), w)
const MAX_WORD = Math.max(...WORDS.map((w) => w.length))

const cache = new Map<string, string>()

function capitalise(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase()
}

/** 'IFCBEAMSTANDARDCASE' → 'IfcBeamStandardCase'. Already-cased names pass through. */
export function prettyIfcClass(raw: string | null | undefined): string {
  if (!raw) return 'Unknown'
  if (raw !== raw.toUpperCase()) return raw
  const cached = cache.get(raw)
  if (cached) return cached

  const upper = raw.toUpperCase()
  const body = upper.startsWith('IFC') ? upper.slice(3) : upper
  // Dynamic programming: fewest words that cover the whole string
  const n = body.length
  const best: (string[] | null)[] = new Array(n + 1).fill(null)
  best[0] = []
  for (let i = 0; i < n; i++) {
    const prev = best[i]
    if (!prev) continue
    for (let len = 1; len <= MAX_WORD && i + len <= n; len++) {
      const word = VOCAB.get(body.slice(i, i + len))
      if (!word) continue
      const candidate = [...prev, word]
      const current = best[i + len]
      if (!current || candidate.length < current.length) best[i + len] = candidate
    }
  }

  let result: string
  const full = best[n]
  if (full) {
    result = full.join('')
  } else {
    // Longest covered prefix + capitalised remainder
    let k = n
    while (k > 0 && !best[k]) k--
    const prefix = (best[k] ?? []).join('')
    const rest = body.slice(k)
    result = prefix + (rest ? (prefix ? rest.toLowerCase() : capitalise(rest)) : '')
  }
  result = (upper.startsWith('IFC') ? 'Ifc' : '') + result
  cache.set(raw, result)
  return result
}
